<template>
  <Card data-test-id="dashboard-program-objectives" class="relative">
    <CardHeader>
      <CardTitle class="flex items-center gap-2">
        <RouteIcon class="size-5 shrink-0" />
        {{ t('dashboard.shared.programObjectives.cardTitle', { name: programRef?.name ?? '' }) }}
      </CardTitle>
      <CardDescription v-if="programRef?.description">
        <span class="line-clamp-1">{{ stripHtml(programRef.description) }}</span>
        <button type="button" data-test-id="dashboard-view-program-button" class="text-primary hover:underline mt-1" @click="modalsStore.toggleModal('program-objective', true)">
          {{ t('dashboard.shared.programObjectives.viewMore') }}
        </button>
      </CardDescription>
    </CardHeader>
    <CardContent>
      <div v-if="isPendingObjectivesRef" class="flex flex-col gap-6">
        <div v-for="i in 3" :key="i" class="flex items-start gap-4">
          <Skeleton class="size-9 shrink-0 rounded-full" />
          <div class="flex flex-col gap-2 pt-1">
            <Skeleton class="h-4 w-40" />
            <Skeleton class="h-3 w-56" />
          </div>
        </div>
      </div>
      <Stepper
        v-else-if="objectivesRef.length > 0"
        :model-value="currentStepRef"
        orientation="vertical"
        :linear="false"
        class="flex w-full flex-col"
      >
        <StepperItem
          v-for="(objective, index) in objectivesRef"
          :key="objective.id"
          v-slot="{ state }"
          :step="index + 1"
          :completed="isObjectiveCompleted(objective)"
          class="flex w-full items-stretch gap-4"
          data-test-id="objective-item"
        >
          <!-- Colonne gauche : cercle + ligne (décoratif : le titre est le contrôle clavier) -->
          <div class="peer/step flex flex-col items-center">
            <Button
              type="button"
              tabindex="-1"
              aria-hidden="true"
              :variant="state === 'completed' || state === 'active' ? 'default' : 'outline'"
              size="icon"
              class="shrink-0 cursor-pointer rounded-full"
              :class="[state === 'active' && 'ring-2 ring-ring ring-offset-2 ring-offset-background']"
              @click="handleShowDescription(objective)"
            >
              <CheckIcon v-if="state === 'completed'" class="size-5" />
              <template v-if="state !== 'completed'">{{ index + 1 }}</template>
            </Button>
            <StepperSeparator
              v-if="index < objectivesRef.length - 1"
              class="mt-2 w-0.5 flex-1 rounded-full bg-muted group-data-[state=completed]:bg-primary"
            />
          </div>

          <!-- Colonne droite : contenu -->
          <div class="flex flex-col gap-1 pb-8">
            <div class="flex items-center gap-2">
              <StepperTitle class="whitespace-normal">
                <Button
                  type="button"
                  variant="link"
                  data-test-id="objective-item-title"
                  class="h-auto p-0 text-sm font-semibold lg:text-base"
                  @click="handleShowDescription(objective)"
                >
                  {{ objective.title }}
                </Button>
              </StepperTitle>
              <Badge variant="outline" class="text-xs" data-test-id="objective-item-progress-text">
                {{ objective.completedSessionsCount }}/{{ objective.expectedSessionCount }}
              </Badge>
            </div>
            <StepperDescription
              v-if="objective.linkedSessions.length > 0"
              class="text-xs text-muted-foreground lg:text-sm"
            >
              <ol class="list-decimal pl-4">
                <li v-for="session in objective.linkedSessions" :key="session.id">
                  {{ formatSessionLabel(session) }}
                </li>
              </ol>
            </StepperDescription>

            <div v-if="canPlanSessionRef && objective.id === nextObjectiveIdRef && objective.completedSessionsCount < objective.expectedSessionCount" class="mt-2">
              <Button
                v-if="!hasMessagesRef"
                data-test-id="objective-item-chat-action"
                :variant="objective.id === nextObjectiveIdRef ? 'default' : 'secondary'"
                size="sm"
                @click.stop="goToChat"
              >
                {{ t('dashboard.shared.programObjectives.exchange') }}
                <MessageSquareIcon class="size-4" />
              </Button>
              <DropdownMenu v-else>
                <DropdownMenuTrigger as-child>
                  <Button
                    data-test-id="objective-item-action"
                    :variant="objective.id === nextObjectiveIdRef ? 'default' : 'secondary'"
                    size="sm"
                    @click.stop
                  >
                    {{ t('dashboard.shared.programObjectives.schedule') }}
                    <CalendarPlusIcon class="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem data-test-id="popover-row" @click="createNewSession(objective)">
                    <CalendarPlusIcon class="size-4" />
                    {{ t('dashboard.shared.programObjectives.createNewSession') }}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-test-id="popover-row" @click="linkExistingSession(objective)">
                    <LinkIcon class="size-4" />
                    {{ t('dashboard.shared.programObjectives.linkExistingSession') }}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </StepperItem>

        <!-- Étape finale : Certificat (fonctionnalité pas encore développée) -->
        <!-- <StepperItem
          v-slot="{ state }"
          :step="objectivesRef.length + 1"
          :completed="allObjectivesCompletedRef"
          class="flex w-full items-center gap-4"
        >
          <div class="flex flex-col items-center">
            <StepperTrigger as-child>
              <Button
                :variant="state === 'completed' || state === 'active' ? 'default' : 'outline'"
                size="icon"
                class="shrink-0 rounded-full"
                :class="[state === 'active' && 'ring-2 ring-ring ring-offset-2 ring-offset-background']"
              >
                <TrophyIcon class="size-5" />
              </Button>
            </StepperTrigger>
          </div>
          <div class="flex flex-col gap-1">
            <StepperTitle
              :class="[state === 'active' && 'text-primary']"
              class="text-sm font-semibold transition lg:text-base"
            >
              Certificat de réussite
            </StepperTitle>
          </div>
        </StepperItem> -->
      </Stepper>
      <p v-else class="py-4 text-center text-sm text-muted-foreground">
        {{ t('dashboard.shared.programObjectives.noObjectives') }}
      </p>
      <LockedOverlay v-if="userGroups.length === 0" :message="t('dashboard.shared.lockedContent.noGroup')" />
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { CalendarPlusIcon, CheckIcon, LinkIcon, MessageSquareIcon, RouteIcon } from 'lucide-vue-next'

import type { V2GroupType } from '@/features/groups/types'
import groupsStore from '@/features/groups/stores/groups/groups.store'
import type { SessionObjectiveWithProgress } from '@/features/programs/api/session-objectives.api'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Stepper, StepperDescription, StepperItem, StepperSeparator, StepperTitle } from '@/components/ui/stepper'
import { Skeleton } from '@/components/ui/skeleton'
import LockedOverlay from '@/features/ui/components/locked-overlay/locked-overlay.vue'
import useDashboardData from '@/features/ui/pages/dashboard/hooks/use-dashboard-data.hook'
import useDashboardPermissions from '@/features/ui/pages/dashboard/hooks/use-dashboard-permissions.hook'
import { selectGetSelectedGroupId } from '@/features/ui/pages/dashboard/stores/dashboard/dashboard.selectors'
import dashboardStore from '@/features/ui/pages/dashboard/stores/dashboard/dashboard.store'
import type { ObjectiveDescriptionModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import { useI18n } from '@/lib/i18n'
import dayjsClient from '@/lib/dayjs'
import { ROUTES } from '@/utils/config/constants'

const { t, locale } = useI18n()
const router = useRouter()

defineProps<{
  userGroups: V2GroupType[]
}>()

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { programRef } = useProgram(selectedProgramIdRef)
const { programRolesRef } = useProgramRoles({ programIdRef: selectedProgramIdRef })

const { canPlanSessionRef } = useDashboardPermissions({
  programRolesRef,
  programRef,
})

const selectedGroupIdRef = selectGetSelectedGroupId(dashboardStore)

const { objectivesRef, hasMessagesRef, conversationIdRef, isPendingObjectivesRef } = useDashboardData({
  selectedGroupIdRef,
})

const nextObjectiveIdRef = computed(() => {
  const nextObjective = objectivesRef.value.find((obj) => obj.completedSessionsCount < obj.expectedSessionCount)
  return nextObjective?.id || null
})

const currentStepRef = computed(() => {
  const nextIndex = objectivesRef.value.findIndex(
    (obj) => obj.completedSessionsCount < obj.expectedSessionCount
  )
  return nextIndex === -1 ? objectivesRef.value.length + 1 : nextIndex + 1
})

const allObjectivesCompletedRef = computed(() => {
  return objectivesRef.value.length > 0 && objectivesRef.value.every(
    (obj) => obj.completedSessionsCount >= obj.expectedSessionCount
  )
})

function stripHtml(html: string) {
  return html.replace(/<[^>]*>/g, '')
}

function isObjectiveCompleted(objective: SessionObjectiveWithProgress) {
  return objective.completedSessionsCount >= objective.expectedSessionCount
}

function formatSessionLabel(session: { name: string | null; from: string }) {
  const dateFormat = locale.value === 'en' ? 'ddd D MMM [at] h:mm A' : 'ddd D MMM [à] HH[h]mm'
  const date = dayjsClient(session.from).format(dateFormat)
  return t('dashboard.shared.programObjectives.sessionLabel', { name: session.name || 'Session', date })
}


function handleShowDescription(objective: SessionObjectiveWithProgress) {
  modalsStore.toggleModal('objective-description', true)
  modalsStore.updateModalAttachedData<ObjectiveDescriptionModalType>('objective-description', {
    title: objective.title,
    description: objective.description || '',
    order: objective.order,
  })
}

function createNewSession(objective: SessionObjectiveWithProgress) {
  if (selectedGroupIdRef.value) {
    groupsStore.setTempSelectedGroupId(selectedGroupIdRef.value)
  }
  groupsStore.setTempSelectedObjectiveId(objective.id)
  modalsStore.toggleModal('new-session', true)
}

function linkExistingSession(objective: SessionObjectiveWithProgress) {
  if (selectedGroupIdRef.value) {
    groupsStore.setTempSelectedGroupId(selectedGroupIdRef.value)
  }
  groupsStore.setTempSelectedObjectiveId(objective.id)
  modalsStore.toggleModal('link-session-to-objective', true)
  modalsStore.updateModalAttachedData('link-session-to-objective', {
    programId: selectedProgramIdRef.value || '',
  })
}

function goToChat() {
  if (!conversationIdRef.value) {
    console.error('No conversationId available')
    return
  }
  router.push(ROUTES.CHAT(conversationIdRef.value))
}
</script>
